import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { format } from 'date-fns';
import { maintenanceService } from '../../../lib/maintenanceService';
import { useAuth } from '../../../contexts/AuthContext';

const MaintenanceAlerts = ({ alerts, onRefresh }) => {
  const { userProfile } = useAuth();

  const getAlertConfig = (alertType) => {
    const configs = {
      upcoming_maintenance: { icon: 'Calendar', color: 'text-primary', bg: 'bg-primary/10' },
      overdue_maintenance: { icon: 'AlertTriangle', color: 'text-error', bg: 'bg-error/10' },
      equipment_failure: { icon: 'AlertCircle', color: 'text-error', bg: 'bg-error/10' },
      parts_needed: { icon: 'Package', color: 'text-warning', bg: 'bg-warning/10' },
      technician_required: { icon: 'Users', color: 'text-primary', bg: 'bg-primary/10' }
    };
    return configs?.[alertType] || configs?.upcoming_maintenance;
  };

  const getPriorityConfig = (priority) => {
    const configs = {
      low: { label: 'Low', color: 'text-muted-foreground' },
      medium: { label: 'Medium', color: 'text-primary' },
      high: { label: 'High', color: 'text-warning' },
      urgent: { label: 'Urgent', color: 'text-error' }
    };
    return configs?.[priority] || configs?.medium;
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await maintenanceService?.acknowledgeAlert(alertId, userProfile?.id);
      onRefresh?.();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    }
  };

  const handleResolve = async (alertId) => {
    try {
      await maintenanceService?.resolveAlert(alertId);
      onRefresh?.();
    } catch (error) {
      console.error('Error resolving alert:', error);
    }
  };

  if (alerts?.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-12 text-center">
        <Icon name="CheckCircle2" size={48} className="text-success mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Active Alerts</h3>
        <p className="text-sm text-muted-foreground">All maintenance alerts have been addressed.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {alerts?.map((alert) => {
        const alertConfig = getAlertConfig(alert?.alert_type);
        const priorityConfig = getPriorityConfig(alert?.priority);

        return (
          <div
            key={alert?.id}
            className="bg-card rounded-xl border border-border p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${alertConfig?.bg}`}>
                <Icon name={alertConfig?.icon} size={20} className={alertConfig?.color} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-semibold ${priorityConfig?.color}`}>
                    {priorityConfig?.label} Priority
                  </span>
                  {alert?.acknowledged_at && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Icon name="Check" size={12} />
                      Acknowledged
                    </span>
                  )}
                </div>
                
                <p className="text-sm font-medium text-foreground mb-2">{alert?.message}</p>
                
                {alert?.equipment && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
                    <Icon name="Package" size={12} />
                    <span>{alert?.equipment?.name}</span>
                  </div>
                )}
                
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Icon name="Clock" size={12} />
                  <span>{format(new Date(alert?.alert_date), 'MMM dd, yyyy h:mm a')}</span>
                </div>
              </div>
            </div>

            {!alert?.acknowledged_at && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Check"
                  onClick={() => handleAcknowledge(alert?.id)}
                  className="flex-1"
                >
                  Acknowledge
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  iconName="CheckCircle2"
                  onClick={() => handleResolve(alert?.id)}
                  className="flex-1"
                >
                  Resolve
                </Button>
              </div>
            )}

            {alert?.acknowledged_at && !alert?.resolved_at && (
              <div className="mt-3 pt-3 border-t border-border">
                <Button
                  variant="primary"
                  size="sm"
                  iconName="CheckCircle2"
                  onClick={() => handleResolve(alert?.id)}
                  className="w-full"
                >
                  Mark as Resolved
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MaintenanceAlerts;